import { Grid, styled, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isMobileDevice } from "../../../commons/utils";
import BarChart from "../../../charts/Bar";
import { Columns, GridCard } from "..";
import { ExCard } from "../../../charts/Card";
import { Calendar1, CalendarDays, CalendarPlus2 } from "lucide-react";
import moment from "moment";
import AreaChart from "../../../charts/area";
import { ExPaper } from "../../../components/Paper";
import { DataTable } from "../../../components/DataTable";

const ToggleButtonSt = styled(ToggleButton)({
    paddingRight: '16px',
    paddingLeft: '16px',
    '&:hover': {
        backgroundColor: '#00141430',
    },
    "&.Mui-selected, &.Mui-selected:hover": {
        color: "white",
        backgroundColor: '#001414'
    }
})

const columns = [
    { field: 'grudescricao', header: 'Empresa', style: { width: window.innerWidth, whiteSpace: 'pre-wrap' } },
    { field: 'countforms', header: 'Formulários', dataType: 'numeric' },
    { field: 'count7', header: 'Semana', dataType: 'numeric' },
    { field: 'count30', header: 'Mês', dataType: 'numeric' },
    { field: 'count90', header: '90 dias', dataType: 'numeric' },    
];

export const DashColetas = forwardRef((props: any, ref) => {
    const [originData, setOriginData] = useState<any[]>([]);
    const [coletaData, setColetaData] = useState<any[]>([])
    const [periodo, setPeriodo] = useState<any>('3m')

    const refMovimento = useRef<any>(null);
    const refHora = useRef<any>(null);
    const refColetasSemana = useRef<any>(null);
    const refColetasMes = useRef<any>(null);
    const refColetasHoje = useRef<any>(null);

    useEffect(() => {
        populateChart(originData);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodo]);

    useImperativeHandle(ref, () => ({
        setData
    }));

    async function sumTotais(_data: any) {
        const totalHoje = _data.filter((e: any) => moment(e.data).isSameOrAfter(moment().startOf('day'))).reduce((acc: number, item: any) => acc + (parseInt(item.total) || 0), 0);
        const totalSemana = _data.filter((e: any) => moment(e.data).isSameOrAfter(moment().startOf('week'))).reduce((acc: number, item: any) => acc + (parseInt(item.total) || 0), 0);
        const totalMes = _data.filter((e: any) => moment(e.data).isSameOrAfter(moment().startOf('month'))).reduce((acc: number, item: any) => acc + (parseInt(item.total) || 0), 0);

        refColetasSemana.current?.setValue(totalSemana);
        refColetasMes.current?.setValue(totalMes);
        refColetasHoje.current?.setValue(totalHoje);
    }

    const getValue = (groupByKey: string | ((item: any) => string), item: any) => typeof groupByKey === 'function' ? groupByKey(item) : item[groupByKey] || 'Não Informado'

    const groupData = (data: any[], groupByKey: string | ((item: any) => string)) => {
        const preGroup = groupDataArr(data, groupByKey)

        const reduced: any = {}
        Object.entries(preGroup).forEach(([key, val]) => reduced[key] = val.reduce((s, e) => s + parseFloat(parseFloat((e.total || 0)).toFixed(2)), 0))

        return reduced
    }

    const groupDataArr = (data: any[], groupByKey: string | ((item: any) => string), media?: boolean) =>
        data.reduce((acc: Record<string, any[]>, item: any) => {
            const key = getValue(groupByKey, item);
            if (!acc[key]) acc[key] = []
            acc[key] = [...acc[key], item];
            return acc;
        }, {});

    function periodFilter(element: any) {
        if (moment(element.data).isAfter(moment(new Date()))) return false

        switch (periodo) {
            case 'year':
                return moment(element.data).isAfter(moment().startOf('year'))
            case '3m':
                return moment(element.data).isAfter(moment().subtract(3, 'months'))
            case '1m':
                return moment(element.data).isAfter(moment().subtract(1, 'months'))
            case '1d':
                return moment(element.data).isSameOrAfter(moment().startOf('day'))
            default:
                return true
        }
    }

    function populateChart(data: any) {
        const _data = data.filter(periodFilter)

        const dataGroup = groupData(_data.map((e: any) => ({ ...e, data: moment(e.data).startOf('day').toDate() })), 'data')
        const horaGroup = groupData(_data.filter((e: any) => parseInt(e.hora) > 0), 'hora')
        const horaGroupHoje = groupData(_data.filter((e: any) => parseInt(e.hora) > 0 && moment(e.data).isSameOrAfter(moment().startOf('day'))), 'hora')

        const valores: any = Object.values(dataGroup) // dataGroup.map((item: any) => parseFloat((item.count || 0).toFixed(2)));
        const periodo = 14
        const valoresMM = valores.map((e: any, idx: number, arr: any) => idx < periodo ? null : (arr.slice(idx - periodo, idx).reduce((s: any, e: any) => s + e, 0) / periodo))
        const descricoes = Object.keys(dataGroup).map(e => moment(e).toDate().getTime()) //dataGroup.map((item: any) => item?.data);

        const valoresH = Object.values(horaGroup).map((e: any) => e / valores.length)
        const descricoesH = Object.keys(horaGroup)

        const valoresHHoje = descricoesH.map(e => horaGroupHoje[e] || 0)

        const getNextMultipleOf100 = (value: number) => { return Math.ceil(value / 100) * 100 };

        refMovimento?.current.setOptions(descricoes, getNextMultipleOf100(Math.max(Math.max(...valores), 0)), 'Coletas Diárias', false, undefined, false, undefined, 'datetime');
        refMovimento?.current.setSeries([{ name: 'Coletas Diárias', type: "area", data: valores }, { name: 'Média Móvel (' + periodo + ' dias)', type: "line", data: valoresMM }]);

        refHora?.current.setOptions(descricoesH, getNextMultipleOf100(Math.max(Math.max(...valoresHHoje), 0)), 'Coletas por Hora do Dia', true, ['var(--primary-color)', '#000000a0'], false, undefined, undefined, { dataLabels: false });
        refHora?.current.setSeries([{ name: 'Coletas por Hora Hoje', type: 'column', data: valoresHHoje }, { name: 'Coletas por Hora do Dia', type: 'area', data: valoresH, zIndex: 999 }]);
        sumTotais(data);
    }

    function setData(_data: any, _coletas: any) {
        setOriginData(_data);
        setColetaData(_coletas)

        populateChart(_data);
    }

    // const columns = [
    //     { field: 'pldescricao', header: 'Plantio' },
    //     { field: 'totalpesagem', header: 'Total Pesagem', body: (e: any) => formatDecimal(e.totalpesagem), dataType: 'numeric' },
    //     { field: 'totalembalagem', header: 'Total Embalagens', body: (e: any) => formatDecimal(e.totalembalagem), dataType: 'numeric' },
    //     { field: 'plarea', header: 'Total Ha', body: (e: any) => formatDecimal(e.plarea), dataType: 'numeric' },
    //     { field: 'pesomedio', header: 'Peso Médio', body: (e: any) => formatDecimal(e.pesomedio), dataType: 'numeric' },
    //     { field: 'pesagemarea', header: 'Pesagem por Ha', body: (e: any) => formatDecimal(e.pesagemarea), dataType: 'numeric' },
    //     { field: 'embalagemarea', header: 'Embalagem por Ha', body: (e: any) => formatDecimal(e.embalagemarea), dataType: 'numeric' },
    // ];

    const handlePeriodo = (e: any, value: any) => {
        setPeriodo(value)
    }

    return (
        <Grid container spacing={4} sx={{ padding: isMobileDevice() ? '1rem' : '1rem 4rem', paddingTop: '32px' }}>
            <Grid item xs={12}>
                <ToggleButtonGroup
                    value={periodo}
                    exclusive
                    color="primary"
                    onChange={handlePeriodo}
                >
                    <ToggleButtonSt value="all"  >
                        Tudo
                    </ToggleButtonSt>
                    <ToggleButtonSt value="year"  >
                        Ano
                    </ToggleButtonSt>
                    <ToggleButtonSt value="3m">
                        3 Meses
                    </ToggleButtonSt>
                    <ToggleButtonSt value="1m" >
                        Mês
                    </ToggleButtonSt>
                    <ToggleButtonSt value="1d">
                        Dia
                    </ToggleButtonSt>
                </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
                <AreaChart ref={refMovimento} />
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <GridCard md={4}>
                        <ExCard ref={refColetasHoje} title='Hoje' icon={<Calendar1 />} />
                    </GridCard>
                    <GridCard md={4}>
                        <ExCard ref={refColetasSemana} title='Esta Semana' icon={<CalendarPlus2 />} />
                    </GridCard>
                    <GridCard md={4}>
                        <ExCard ref={refColetasMes} title='Este Mês' icon={<CalendarDays />} />
                    </GridCard>
                </Grid>
            </Grid>
            <Grid item xs={6}>
                <BarChart ref={refHora} />
            </Grid>
            <Grid item xs={6}>
                <ExPaper>
                    <DataTable value={coletaData} originvalues={coletaData} disabledrefresh={true}>
                        {Columns(columns)}
                    </DataTable>
                </ExPaper>
            </Grid>
        </Grid>
    )
})